import React from 'react';
import { useTranslation } from 'react-i18next';
import { getBerlinToday, addDaysToDateOnly, formatDateOnly } from '../../../utils/berlinDate';

// Keys are day offsets, not date-range preset names, so these cannot reuse
// common:datePresets.labels wholesale. today/yesterday are the same words and
// do reuse it; the "N days ago" wording is specific to this control.
const DAY_OFFSET_PRESETS = [
  { key: 0, labelKey: 'common:datePresets.labels.today' },
  { key: -1, labelKey: 'common:datePresets.labels.yesterday' },
  { key: -7, labelKey: 'filter:dayOffsets.d7' },
  { key: -14, labelKey: 'filter:dayOffsets.d14' },
  { key: -30, labelKey: 'filter:dayOffsets.d30' },
];

const PILL_CLASS =
  'px-3 py-1.5 text-xs font-medium border border-gray-300 rounded-full ' +
  'text-gray-700 hover:bg-gray-100 hover:border-gray-400 transition-colors';

const ACTIVE_PILL_CLASS =
  'px-3 py-1.5 text-xs font-medium border border-primary-500 rounded-full ' +
  'bg-primary-50 text-primary-700';

const DISABLED_PILL_CLASS =
  'px-3 py-1.5 text-xs font-medium border border-gray-200 rounded-full ' +
  'text-gray-400 cursor-not-allowed';

// Sentinel offset for the "last active day" pill — unlike DAY_OFFSET_PRESETS'
// numeric keys (resolved against calendar-today), this resolves against
// whatever date the caller passes as lastActiveDate (see
// useResourceStatusMonitor.js's resourceLastActiveDate/resolveRollingDate —
// resource-scoped, NOT the whole dataset's last event date). Value must stay
// in sync with backend/src/shared/cache_warming.py's _resolve_rolling_date,
// which mirrors this exact string for cache-warm target resolution.
export const LAST_ACTIVE_DAY_PRESET = 'lastActiveDay';

/**
 * RollingDayPresets - "Rollierend" quick-select buttons for a single anchor
 * date (e.g. selectedDate), as opposed to DateRangePresets which sets a
 * {startDate, endDate} range. Berlin-calendar-day-aligned.
 *
 * @param {number|string|null} [value] - Currently active offset (one of
 *   DAY_OFFSET_PRESETS' keys, LAST_ACTIVE_DAY_PRESET, or null/undefined if
 *   none/manual) — highlights the matching button. Optional: omit to keep the
 *   old no-highlight behavior.
 * @param {Function} onChange - Callback invoked as (dateString, offset) with the
 *   computed "YYYY-MM-DD" date and the offset that produced it. Consumers that
 *   only need the date string can keep a single-argument handler — the extra
 *   argument is simply ignored.
 * @param {string|null} [lastActiveDate] - When explicitly passed (even null),
 *   renders an extra "last active day" pill that resolves to this date instead
 *   of a today-relative offset. The caller decides its scope (e.g.
 *   ResourceStatusMonitor scopes it to the tile's own configured resources).
 *   Omit entirely to keep prior consumers (e.g. ExecutiveResourceUtilizationTile)
 *   rendering unchanged.
 */
const RollingDayPresets = ({ value, onChange, lastActiveDate }) => {
  const { t } = useTranslation();

  const applyPreset = (offset) => {
    const today = getBerlinToday();
    const target = addDaysToDateOnly(today, offset);
    onChange(formatDateOnly(target), offset);
  };

  const applyLastActiveDay = () => {
    if (!lastActiveDate) return;
    onChange(lastActiveDate, LAST_ACTIVE_DAY_PRESET);
  };

  return (
    <div className="space-y-1.5">
      <label className="block text-xs font-medium text-gray-500">
        {t('common:datePresets.groups.rolling')}
      </label>
      <div className="flex flex-wrap gap-2">
        {DAY_OFFSET_PRESETS.map(({ key, labelKey }) => (
          <button
            key={key}
            type="button"
            onClick={() => applyPreset(key)}
            className={value === key ? ACTIVE_PILL_CLASS : PILL_CLASS}
          >
            {t(labelKey)}
          </button>
        ))}
        {lastActiveDate !== undefined && (
          <button
            type="button"
            onClick={applyLastActiveDay}
            disabled={!lastActiveDate}
            className={
              !lastActiveDate
                ? DISABLED_PILL_CLASS
                : value === LAST_ACTIVE_DAY_PRESET
                  ? ACTIVE_PILL_CLASS
                  : PILL_CLASS
            }
          >
            {t('filter:dayOffsets.lastActiveDay')}
          </button>
        )}
      </div>
    </div>
  );
};

export default RollingDayPresets;
